/**
 * CNC Export Engine — Production-ready panel data for CNC machines
 * Exports CSV, structured JSON with hole positions, groove locations, hinge cups
 */
import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Cpu, Download, FileText } from 'lucide-react';

function generateCNCData(cutList, config) {
  const hingeCupDia = 35; // mm
  const hingeCupDepth = 12; // mm
  const shelfPinDia = 5;
  const shelfPinDepth = 10;
  const shelfPinSpacing = 32; // mm (32mm system)
  const grooveWidth = 8; // mm for back panel
  const grooveDepth = 10;
  const edgeOffset = 37; // mm from edge for hinge cups

  return cutList.map((part, idx) => {
    const operations = [];
    const holes = [];
    const grooves = [];

    // Side panels: shelf pin holes + back panel groove + hinge cup boring
    if (part.code === 'C1' || part.code === 'C2') {
      // Shelf pin holes (2 rows, 32mm system)
      const frontRow = 50; // mm from front edge
      const backRow = part.w - 50;
      for (let y = 200; y < part.h - 200; y += shelfPinSpacing) {
        holes.push({ x: frontRow, y, dia: shelfPinDia, depth: shelfPinDepth, type: 'shelf_pin' });
        holes.push({ x: backRow, y, dia: shelfPinDia, depth: shelfPinDepth, type: 'shelf_pin' });
      }
      // Back panel groove (along back edge)
      grooves.push({ x: part.w - 15, y1: 0, y2: part.h, width: grooveWidth, depth: grooveDepth, type: 'back_panel' });
      // Hinge cup holes (for shutters)
      if (part.code === 'C1') {
        const hingeCount = Math.ceil((part.h / 25.4 / 12) / 1.5); // one per 18 inches
        for (let i = 0; i < hingeCount; i++) {
          const y = 100 + (i * (part.h - 200) / Math.max(hingeCount - 1, 1));
          holes.push({ x: edgeOffset, y: Math.round(y), dia: hingeCupDia, depth: hingeCupDepth, type: 'hinge_cup' });
        }
      }
      operations.push('drill_shelf_pins', 'route_groove', 'bore_hinge_cups');
    }

    // Drawer sides: groove for bottom panel
    if (part.code.match(/^D\d+-[LR]$/)) {
      grooves.push({ x: 0, y1: part.h - 15, y2: part.h - 15, width: grooveWidth, depth: grooveDepth, type: 'drawer_bottom' });
      operations.push('route_groove');
    }
    // Drawer front/back: groove for bottom
    if (part.code.match(/^D\d+-[FB]$/)) {
      grooves.push({ x: 0, y1: part.h - 15, y2: part.h - 15, width: grooveWidth, depth: grooveDepth, type: 'drawer_bottom' });
      operations.push('route_groove');
    }

    // Shutter panels: hinge cup boring
    if (part.code.startsWith('SH')) {
      const hingeCount = Math.ceil((part.h / 25.4 / 12) / 1.5);
      for (let i = 0; i < hingeCount; i++) {
        const y = 100 + (i * (part.h - 200) / Math.max(hingeCount - 1, 1));
        holes.push({ x: edgeOffset, y: Math.round(y), dia: hingeCupDia, depth: hingeCupDepth, type: 'hinge_cup' });
      }
      operations.push('bore_hinge_cups');
    }

    return {
      sequence_id: idx + 1,
      part_code: part.code,
      part_name: part.part,
      material: part.material,
      width_mm: part.w,
      height_mm: part.h,
      thickness: part.material.includes('18') ? 18 : part.material.includes('12') ? 12 : part.material.includes('8') ? 8 : part.material.includes('16') ? 16 : 18,
      laminate: part.laminate || 'None',
      holes,
      grooves,
      operations,
      edge_banding: part.code.startsWith('SH') ? 'All 4 sides' : part.part.includes('Shelf') ? 'Front edge' : part.part.includes('Side') ? 'Front + top' : 'None',
    };
  });
}

export default function CNCExportEngine({ cutList = [], config = {} }) {
  const [machineBrand, setMachineBrand] = useState('universal');

  const cncData = useMemo(() => generateCNCData(cutList, config), [cutList, config]);

  const exportCSV = () => {
    const headers = ['Sequence', 'Code', 'Part', 'Material', 'Width(mm)', 'Height(mm)', 'Thickness(mm)', 'Holes', 'Grooves', 'Operations', 'Edge Banding'];
    const rows = cncData.map(p => [
      p.sequence_id, p.part_code, p.part_name, p.material, p.width_mm, p.height_mm, p.thickness,
      p.holes.length, p.grooves.length, p.operations.join(';'), p.edge_banding,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'CNC_Panel_Data.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const exportJSON = () => {
    const json = JSON.stringify({ machine: machineBrand, panels: cncData, generated: new Date().toISOString() }, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'CNC_Panel_Data.json'; a.click();
    URL.revokeObjectURL(url);
  };

  const totalHoles = cncData.reduce((s, p) => s + p.holes.length, 0);
  const totalGrooves = cncData.reduce((s, p) => s + p.grooves.length, 0);
  const panelsWithOps = cncData.filter(p => p.operations.length > 0).length;

  return (
    <div className="space-y-5" data-testid="cnc-export-engine">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Cpu size={18} className="text-cyan-600" /><h3 className="font-bold text-stone-800">CNC Export Engine</h3></div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={exportCSV} data-testid="export-cnc-csv"><Download size={14} className="mr-1" />CSV</Button>
          <Button size="sm" variant="outline" onClick={exportJSON} data-testid="export-cnc-json"><FileText size={14} className="mr-1" />JSON</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total Panels', value: cncData.length, color: 'bg-cyan-50 text-cyan-800' },
          { label: 'Drill Holes', value: totalHoles, color: 'bg-blue-50 text-blue-800' },
          { label: 'Routing Grooves', value: totalGrooves, color: 'bg-violet-50 text-violet-800' },
          { label: 'CNC Operations', value: panelsWithOps, color: 'bg-emerald-50 text-emerald-800' },
        ].map((s, i) => (
          <div key={i} className={`p-3 rounded-lg ${s.color}`}>
            <p className="text-[10px] uppercase font-bold opacity-70">{s.label}</p>
            <p className="text-xl font-extrabold">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Panel details table */}
      <Card>
        <CardContent className="pt-4 pb-2 px-2">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="cnc-panel-table">
              <thead><tr className="bg-stone-50 border-b">
                {['#', 'Code', 'Part', 'W', 'H', 'Thk', 'Holes', 'Grooves', 'Operations', 'Edge Band'].map(h => (
                  <th key={h} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {cncData.map((p, i) => (
                  <tr key={i} className="border-b hover:bg-stone-50">
                    <td className="px-2 py-1.5 text-stone-400">{p.sequence_id}</td>
                    <td className="px-2 py-1.5 font-mono font-bold text-cyan-700">{p.part_code}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{p.part_name}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{p.width_mm}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{p.height_mm}</td>
                    <td className="px-2 py-1.5">{p.thickness}mm</td>
                    <td className="px-2 py-1.5 text-center">{p.holes.length > 0 ? <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-[10px] font-bold">{p.holes.length}</span> : '-'}</td>
                    <td className="px-2 py-1.5 text-center">{p.grooves.length > 0 ? <span className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full text-[10px] font-bold">{p.grooves.length}</span> : '-'}</td>
                    <td className="px-2 py-1.5 text-stone-500">{p.operations.join(', ') || '-'}</td>
                    <td className="px-2 py-1.5 text-stone-500">{p.edge_banding}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
